import { useCallback, useEffect, useRef, useState } from 'react';
import KuestionMark from '../kuestion/KuestionMark';
import terminalxIcon from '../../assets/images/terminalx/app.svg';
import './HomeSections.css';

const ITIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" />
  </svg>
);
const EduIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 10 12 5 2 10l10 5 10-5Z" /><path d="M6 12v5c0 1.66 2.69 3 6 3s6-1.34 6-3v-5" />
  </svg>
);
const ProIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="20" height="14" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
  </svg>
);
const FinIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="2" x2="12" y2="22" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);
const MedIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4.8 9.5h3l1.7 4.5 2.4-8 2 6 1.3-2.5h3.9" /><path d="M20.8 12.5A8.8 8.8 0 1 1 12 3.7" />
  </svg>
);
const LogIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 3h12v12H1z" /><path d="M13 7h5l3 3.5V15h-8" /><circle cx="6" cy="18.5" r="2" /><circle cx="17" cy="18.5" r="2" />
  </svg>
);

const INDUSTRIES = [
  { icon: <ITIcon />, title: 'IT', body: 'Backup, recovery and estate housekeeping that runs itself and tells you when it didn\u2019t.' },
  { icon: <EduIcon />, title: 'Education', body: 'Examination, testing and reporting for teachers and candidates \u2014 this is where Kuestion lives.' },
  { icon: <ProIcon />, title: 'Professionals', body: 'Document, reporting and workflow tools for small practices that have no IT department.' },
  { icon: <FinIcon />, title: 'Finance', body: 'Reconciliation, statement handling and audit trails that assemble themselves each month.' },
  { icon: <MedIcon />, title: 'Healthcare', body: 'Records, scheduling and compliance paperwork for clinics running without a back office.' },
  { icon: <LogIcon />, title: 'Logistics', body: 'Dispatch, proof of delivery and exception handling tracked from one place.' },
];

export function WhatWeDo() {
  return (
    <section className="rw-section" id="rw-what">
      <div className="rw-section__inner rw-what">
        <div className="rw-what__head" data-rise="0.4">
          <div className="rw-kicker">What Riftworks does</div>
          <h2>We build software that takes the task off your desk.</h2>
          <p>
            Riftworks is a SaaS company easing daily work across several industries. Every product
            starts from the same question: which part of this job should a person never have to do twice?
          </p>
        </div>
        <Industries />
      </div>
    </section>
  );
}

function Industries() {
  const [index, setIndex] = useState(0);
  const [per, setPer] = useState(3);
  const hold = useRef(false);
  const box = useRef(null);

  const measure = useCallback(() => {
    const w = box.current ? box.current.clientWidth : window.innerWidth;
    setPer(w < 620 ? 1 : w < 1000 ? 2 : 3);
  }, []);

  useEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  const max = Math.max(0, INDUSTRIES.length - per);
  useEffect(() => { setIndex((i) => (i > max ? 0 : i)); }, [max]);

  useEffect(() => {
    const t = setInterval(() => {
      if (!hold.current) setIndex((i) => (i >= max ? 0 : i + 1));
    }, 4200);
    return () => clearInterval(t);
  }, [max]);

  const step = (dir) => setIndex((i) => {
    const n = i + dir;
    if (n < 0) return max;
    if (n > max) return 0;
    return n;
  });

  return (
    <div
      className="rw-carousel"
      ref={box}
      data-rise="0.6"
      onMouseEnter={() => { hold.current = true; }}
      onMouseLeave={() => { hold.current = false; }}
    >
      <div className="rw-carousel__viewport">
        <div
          className="rw-carousel__track"
          style={{ transform: 'translateX(' + (-index * (100 / per)) + '%)' }}
        >
          {INDUSTRIES.map((item) => (
            <article
              className="rw-carousel__cell"
              key={item.title}
              style={{ flex: '0 0 ' + (100 / per) + '%' }}
            >
              <span className="rw-carousel__icon">{item.icon}</span>
              <h3>{item.title}</h3>
              <p>{item.body}</p>
            </article>
          ))}
        </div>
      </div>

      <div className="rw-carousel__controls">
        <button type="button" className="rw-carousel__arrow" onClick={() => step(-1)} aria-label="Previous industries">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
        </button>
        <button type="button" className="rw-carousel__arrow" onClick={() => step(1)} aria-label="Next industries">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
        </button>
        <div className="rw-carousel__dots">
          {Array.from({ length: max + 1 }).map((_, i) => (
            <button
              type="button"
              key={i}
              className={'rw-carousel__dot' + (i === index ? ' is-active' : '')}
              aria-label={'Go to industry ' + (i + 1)}
              onClick={() => setIndex(i)}
            />
          ))}
        </div>
        <span className="rw-carousel__note">{INDUSTRIES.length} industries &middot; auto-advancing</span>
      </div>
    </div>
  );
}

export function AgenticAI() {
  return (
    <section className="rw-section rw-agentic">
      <div className="rw-section__inner rw-agentic__inner">
        <div data-rise="0.5">
          <div className="rw-kicker">Agentic AI</div>
          <h2 className="rw-agentic__title">Coding and AI, pointed at the day to day.</h2>
          <p className="rw-agentic__body">
            Our focus is agentic AI development: software that carries a task from start to finish,
            checks its own work and hands you the result &mdash; not another dashboard to read.
          </p>
          <p className="rw-agentic__body">
            The agents sit inside the products you already use. Nothing new to learn,
            nothing extra to install.
          </p>
        </div>
        <div className="rw-agentic__list" data-rise="1" data-yaw="0.8">
          <div><strong>It plans before it acts</strong><span>Every run shows its steps, so you can stop it at step two.</span></div>
          <div><strong>It asks once, not constantly</strong><span>One decision from you, then it finishes the job.</span></div>
          <div><strong>It leaves a record</strong><span>What ran, what changed, what needs a human &mdash; in writing.</span></div>
        </div>
      </div>
    </section>
  );
}

export function AppsStrip({ onOpen, onOpenTerminalx }) {
  const onKey = (fn) => (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fn(); } };
  return (
    <section className="rw-section">
      <div className="rw-section__inner">
        <div className="rw-kicker rw-apps-strip__kicker">Applications</div>
        <div className="rw-apps-strip" data-rise="1" data-yaw="0.5" onClick={onOpen} role="button" tabIndex={0}
          onKeyDown={onKey(onOpen)}>
          <div className="rw-apps-strip__main">
            <span className="rw-apps-strip__tile">
              <KuestionMark bodyFill="currentColor" leafFill="currentColor" glyphFill="var(--color-accent)" />
            </span>
            <h2>Kuestion</h2>
            <p>
              Examination preparation, candidate testing and self-scoring for the education sector.
              Teachers build and push exams; candidates sit them and see exactly where they stand.
            </p>
            <span className="rw-btn rw-btn--ghost">Open the application page &rarr;</span>
          </div>
          <div className="rw-apps-strip__facts">
            <div>Examiner mode &mdash; build, design, push</div>
            <div>Student mode &mdash; sit, self-score, review</div>
            <div>Passing grades &amp; topic strengths</div>
            <div>One PDF report per candidate</div>
          </div>
        </div>
        <div className="rw-apps-strip rw-apps-strip--next" data-rise="1" data-yaw="-0.5" onClick={onOpenTerminalx} role="button" tabIndex={0}
          onKeyDown={onKey(onOpenTerminalx)}>
          <div className="rw-apps-strip__main">
            <img className="rw-apps-strip__icon" src={terminalxIcon} alt="" width="60" height="60" />
            <h2>TerminalX</h2>
            <p>
              The console client with an engineer's assistant built in. Serial, telnet and SSH in one
              native Windows window, with an AI Helper that reads the same screen you do.
            </p>
            <span className="rw-btn rw-btn--ghost">Open the application page &rarr;</span>
          </div>
          <div className="rw-apps-strip__facts">
            <div>Serial, telnet, SSH and local shell</div>
            <div>A Helper that answers, looks and drafts changes</div>
            <div>Destructive commands stopped in code</div>
            <div>IP scanner and TFTP server built in</div>
          </div>
        </div>
        <p className="rw-apps-strip__more">More applications are in build for IT and professional services.</p>
      </div>
    </section>
  );
}

export function PosterCTA({ title, body, action, onAction, href }) {
  return (
    <section className="rw-section rw-poster">
      <div className="rw-section__inner rw-poster__inner">
        <h2 data-rise="0.8" data-yaw="-0.5">{title}</h2>
        <div data-rise="0.8" data-yaw="0.5">
          <p className="rw-poster__body">{body}</p>
          {href ? (
            <a href={href} className="rw-btn rw-btn--invert">{action}</a>
          ) : (
            <button type="button" className="rw-btn rw-btn--invert" onClick={onAction}>{action}</button>
          )}
        </div>
      </div>
    </section>
  );
}
